import { logger } from '../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Layers, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';
import SeriesList from './dicom/SeriesList';
import SliceViewer from './dicom/SliceViewer';

const DicomSeriesViewer = ({ patientId, examId }) => {
  const [seriesList, setSeriesList] = useState([]);
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [seriesDetail, setSeriesDetail] = useState(null);
  const [currentSlice, setCurrentSlice] = useState(0);
  const [sliceUrl, setSliceUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newSeriesName, setNewSeriesName] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchSeries = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/images/series/patient/${patientId}`, { withCredentials: true });
      setSeriesList(data.series || []);
    } catch (error) {
      logger.error('Error fetching series:', error);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { fetchSeries(); }, [fetchSeries]);

  const loadSlice = async (seriesId, index) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/images/series/${seriesId}/slice/${index}`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      setSliceUrl(prev => { if (prev) URL.revokeObjectURL(prev); return url; });
    } catch (error) {
      logger.error('Failed to load slice:', error);
    }
  };

  const handleSelectSeries = async (seriesId) => {
    setSelectedSeries(seriesId);
    setCurrentSlice(0);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/images/series/${seriesId}`, { withCredentials: true });
      setSeriesDetail(data);
      if (data.slice_count > 0) loadSlice(seriesId, 0);
    } catch (error) {
      toast.error('Failed to load series');
    }
  };

  const handleSliceChange = (newIndex) => {
    if (!seriesDetail || newIndex < 0 || newIndex >= seriesDetail.slice_count) return;
    setCurrentSlice(newIndex);
    loadSlice(selectedSeries, newIndex);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!seriesDetail) return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        handleSliceChange(currentSlice + 1);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        handleSliceChange(currentSlice - 1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  const handleCreateSeries = async (e) => {
    e.preventDefault();
    if (!newSeriesName.trim()) return;
    setCreating(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/images/series/create`, {
        patient_id: patientId, series_name: newSeriesName, exam_id: examId
      }, { withCredentials: true });
      toast.success('Series created');
      setShowCreate(false);
      setNewSeriesName('');
      fetchSeries();
      handleSelectSeries(data.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create series');
    } finally {
      setCreating(false);
    }
  };

  const handleUploadSlice = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !selectedSeries) return;
    setUploading(true);
    try {
      const startIndex = seriesDetail?.slice_count || 0;
      for (let i = 0; i < files.length; i++) {
        const formData = new FormData();
        formData.append('file', files[i]);
        await axios.post(
          `${BACKEND_URL}/api/images/series/${selectedSeries}/upload-slice?slice_index=${startIndex + i}`,
          formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
        );
      }
      toast.success(`Uploaded ${files.length} slice(s)`);
      handleSelectSeries(selectedSeries);
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[#4B6651]" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="dicom-series-viewer">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] flex items-center gap-2">
          <Layers className="w-5 h-5 text-[#4B6651]" />
          DICOM Series
        </h3>
        <Button size="sm" onClick={() => setShowCreate(!showCreate)} className="bg-[#4B6651] hover:bg-[#3E5543] text-white" data-testid="new-series-btn">
          <Plus className="w-4 h-4 mr-1" />
          New Series
        </Button>
      </div>

      {showCreate && (
        <Card className="border-[#4B6651] bg-white">
          <CardContent className="p-4">
            <form onSubmit={handleCreateSeries} className="flex gap-3">
              <Input
                placeholder="Series name (e.g., MRI Brain T2 Sagittal)"
                value={newSeriesName}
                onChange={(e) => setNewSeriesName(e.target.value)}
                className="flex-1 border-[#E5E4E0]"
                data-testid="series-name-input"
              />
              <Button type="submit" disabled={creating} size="sm" className="bg-[#4B6651] hover:bg-[#3E5543] text-white" data-testid="create-series-btn">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-1">
          <SeriesList seriesList={seriesList} selectedSeries={selectedSeries} onSelectSeries={handleSelectSeries} />
        </div>
        <div className="lg:col-span-3">
          <SliceViewer
            seriesDetail={seriesDetail}
            sliceUrl={sliceUrl}
            currentSlice={currentSlice}
            onSliceChange={handleSliceChange}
            uploading={uploading}
            onUploadSlice={handleUploadSlice}
          />
        </div>
      </div>
    </div>
  );
};

export default DicomSeriesViewer;
